import React, { useCallback, useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import { Provider, connect, useSelector } from 'react-redux'
import store from 'store'
import { ConfigProvider as AntdProvider, theme } from 'antd'

import english from 'locales/en-US'
import indonesia from 'locales/id-ID'
import { optionsSelector } from 'app-redux/selectors'
import { allowedLocales } from 'utils/static'
import { changeLocale } from 'utils/helper'
import { store as reduxStore } from '@/store'

const { defaultAlgorithm, darkAlgorithm } = theme

const locales = {
  'en-US': english,
  'id-ID': indonesia,
}

const getThemeConfig = (isDarkMode) =>
  Object.freeze({
    algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
    token: {
      colorPrimary: '#4677f3',
      fontSize: 13,
    },
    components: {
      Table: {
        headerBg: isDarkMode ? '#424242' : '#fafafa',
        rowHoverBg: isDarkMode ? '#424242' : '#fafafa',
      },
      Tooltip: {
        zIndexPopup: 2071,
        colorBgSpotlight: 'rgba(0, 0, 0, 0.75)',
      },
      DatePicker: {
        cellHoverWithRangeBg: isDarkMode ? '#424242' : '',
      },
    },
  })

// Modal.confirm, message, dan notification statis dirender di luar tree React,
// jadi tidak dapat theme dari AntdProvider di bawah. Holder message dibuat sekali,
// jadi theme dibaca langsung dari redux supaya ikut berubah saat dark mode diganti.
const StaticHolderTheme = ({ children }) => {
  const options = useSelector(optionsSelector)

  return <AntdProvider theme={getThemeConfig(options?.dark_mode === 1)}>{children}</AntdProvider>
}

AntdProvider.config({
  holderRender: (children) => (
    <Provider store={reduxStore.store}>
      <StaticHolderTheme>{children}</StaticHolderTheme>
    </Provider>
  ),
})

const ConfigProvider = ({ children, settings }) => {
  const options = useSelector(optionsSelector)

  const setLocale = useCallback(() => {
    changeLocale(settings.locale)
  }, [settings.locale])

  const getPopupContainer = useCallback(() => document.getElementById('scrollable-container'), [])

  const setDarkMode = useCallback(() => {
    if (options && options?.dark_mode === 1) {
      store.set('app.darkMode', 1)
      document.documentElement.classList.add('dark')
      document.documentElement.setAttribute('data-color-scheme', 'dark')
    } else if (options && options?.dark_mode === 0) {
      store.remove('app.darkMode')
      document.documentElement.classList.remove('dark')
      document.documentElement.setAttribute('data-color-scheme', 'light')
    }
  }, [options])

  useEffect(() => {
    setLocale()
  }, [setLocale])

  useEffect(() => {
    if (options) {
      setDarkMode()
    }
  }, [options, setDarkMode])

  const currentLocale =
    locales[allowedLocales.includes(settings.locale) ? settings.locale : 'id-ID']

  return (
    <AntdProvider
      getPopupContainer={getPopupContainer}
      locale={currentLocale.antdData}
      theme={getThemeConfig(options?.dark_mode === 1)}
    >
      <IntlProvider locale={currentLocale.locale} messages={currentLocale.messages}>
        {children}
      </IntlProvider>
    </AntdProvider>
  )
}

export default connect(({ settings }) => ({ settings }))(ConfigProvider)
